import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useRef, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { patchAccessory } from '@services/queries/onboarding/patchAccessory';

import ReactTooltip from 'react-tooltip';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Line,
  Confirm,
  Checkbox,
  CheckContainer,
  CheckText,
  WarningContainer,
  Description,
  CheckTextDescription,
  CheckDivider,
  TextsRowFlex,
  TextsRowFlexItem,
  ErrorStatus,
  TextTitle,
  TextDescription,
  ToolTipText,
  Cancel,
} from './ErrorsFoundDialog.styles';
import Labels from '../Labels/Labels';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { OnboardingFileContext } from '../OnboardingFile/OnboardingFile.query';

interface IErrorsFoundDialog {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  onSkip: () => void;
  isLoading: boolean;
  closeFlow: () => void;
}

export const ErrorsFoundDialog = ({
  open,
  onClose,
  group,
  onSkip,
  isLoading,
  closeFlow,
}: IErrorsFoundDialog) => {
  const portalDiv = document.getElementById('portal');

  const { fieldErrorSelected, errorManufacturerDialog } = useContext(
    OnboardingFileContext,
  );

  const errorTooltipRef: any = useRef(null);

  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string | null>(null);
  const [openSkip, setOpenSkip] = useState<boolean>(false);

  const getTextError = () => {
    if (group.mfrError?.includes('Invalid')) {
      return 'Invalid';
    }
    if (group.mfrError?.includes('Not Found')) {
      return 'Not Found';
    }

    return 'Empty';
  };

  const accessoryMutation = useMutation(patchAccessory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      onClose();
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <WarningContainer>
                <BlueIcon icon="error" size={16} color="#D14343" />
              </WarningContainer>
              <Title>1 Error found</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              <TextsRowFlexItem>
                <TextTitle>Manufacturer Error</TextTitle>
                <TextDescription>{group.manufacturer}</TextDescription>
              </TextsRowFlexItem>

              <TextsRowFlexItem>
                <ToolTipText
                  ref={errorTooltipRef}
                  data-tip="Manufacturer is either incorrect or does not exist."
                />
                <ReactTooltip effect="solid" globalEventOff="click" />
                <ErrorStatus
                  onMouseOver={() => ReactTooltip.show(errorTooltipRef.current)}
                  onMouseLeave={() => {
                    ReactTooltip.hide(errorTooltipRef.current);
                  }}
                >
                  <Labels text={getTextError()} type="error" />
                </ErrorStatus>
              </TextsRowFlexItem>
            </TextsRowFlex>

            <Description>
              We couldn’t identify the Manufacturer for this product. Without
              identifying this, we cannot provide data like Contacts and
              Resources. How would you like to proceed:
            </Description>

            <CheckContainer onClick={() => setSelected('fix')}>
              <Checkbox active={selected === 'fix'} />
              <CheckText>Fix error</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              You can select the correct manufacturer by choosing between
              suggestions or searching for it in our database. Once the
              manufacturer is matched, you will be able to access data like
              Contacts and Resources.
            </CheckTextDescription>

            <CheckDivider />

            <CheckContainer onClick={() => setSelected('skip')}>
              <Checkbox active={selected === 'skip'} />
              <CheckText>Skip error</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              If you choose not to fix the manufacturer, it will be added as an
              Internal. <b>Internal Manufacturers</b> are just for reference and
              have no data from us.
            </CheckTextDescription>

            <Line>
              <Cancel type="button" onClick={onClose}>
                Back
              </Cancel>
              <Confirm
                type="button"
                disabled={!selected || accessoryMutation.isLoading}
                onClick={() => {
                  if (selected === 'fix') {
                    // selectedAsset.set(asset);
                    fieldErrorSelected.set('manufacturer');
                    errorManufacturerDialog.set(true);
                    closeFlow();
                  }
                  if (selected === 'skip') {
                    setOpenSkip(true);
                  }
                }}
              >
                Confirm
              </Confirm>
            </Line>
          </Container>
          {openSkip && (
            <ConfirmDialog
              open={openSkip}
              onClose={() => setOpenSkip(false)}
              isLoading={isLoading}
              onConfirm={onSkip}
              showAsset
              isLarge
              internalManufacturer
              group={group}
              title="Internal Product added"
              description="This type of product is added immediatelly, but no additional product data can be included by Beam. However, you’ll be able to add your own information."
            />
          )}
        </Overlay>,
        portalDiv,
      )
    : null;
};
